import { Injectable } from '@angular/core';
import { Observable, Subscription, of, BehaviorSubject, combineLatest } from 'rxjs';
import { map, first, switchMap, pluck, shareReplay, tap, filter, distinctUntilChanged } from 'rxjs/operators';

import { Challenge, MotesPaths, User, MoteEventType, MoteEvent, QuizPointMoteEvent, ChallengeStateMap, ChallengeState, Mote, PointOptions } from '../models';
import { DatabaseService } from './database.service';
import { ProjectChangeEv } from './project.service';
import { MoteEventService } from './mote-event.service';
import { UserService } from './user.service';
import { GroupService } from './group.service';
import { AutoSub, AutoSubOnDestroy } from './auto-sub';

export interface QuizQuestionState { mote: Mote, available: boolean, completed: boolean }

/**
 * Functionality related to challenges (including quizzes)
 */
@AutoSubOnDestroy()
@Injectable({ providedIn: 'root' })
export class QuizService {
    // Indicate
    public quizPopupShown: boolean = false;

    private challengeMap: { [pointType: string]: Challenge[] } = {};
    private user: User = null;

    private quizQuestionState: BehaviorSubject<QuizQuestionState> = new BehaviorSubject<QuizQuestionState>(null);

    constructor(private db: DatabaseService,
        private moteEventService: MoteEventService,
        private userService: UserService,
        private groupService: GroupService) {
    }

    /**
     * Init the challenge service point / mote event listening mechanism to register and update user challenge points
     */
    public async init(projectObs: Observable<ProjectChangeEv>, watchPublicOnly = true) {
        // Watch user points
        AutoSub(this).reg['userStateMapSub'] = this.watchUserChallengeStateMap().subscribe();
        AutoSub(this).reg['moteEvents'] = this.handleMoteEvents();
        AutoSub(this).reg['pointTriggerSub'] = this.watchPointTriggers();
        AutoSub(this).reg['challengeMapSub'] = this.watchChallengeMap(watchPublicOnly, projectObs);
    }

    private watchChallengeMap(onlyPublished = true, projectObs: Observable<ProjectChangeEv>) {
        return this.watchActiveProjectChallenges(projectObs, onlyPublished).subscribe((challenges) => {
            this.challengeMap = {};
            // Generate map of challenges associated by mote trigger events
            for(let challenge of challenges) {
                let list = this.challengeMap[challenge.pointType];
                // Init list if not already initialized
                if(!list) list = this.challengeMap[challenge.pointType] = [];
                // Add challenge to map of events triggered by this type of mote event
                list.push(challenge);
            }
        });
    }

    /**
     * Watch the current user's or group user's challenge state map containing quiz points
     * @returns Observable of the current users challenge state
     */
    public watchUserChallengeStateMap(): Observable<ChallengeStateMap> {
        console.log("watchUserChallengeStateMap")
        let lastChallengeState: string = null;

        return this.userService.watchCurrentUser().pipe(
            // Save current user reference for convenience (when saving quiz data)
            tap((user) => this.user = user),
            // Pick challenge state map from group or directly from user
            switchMap((user) => (user?.groupKey) ? this.groupService.watchCurrentMember() : of(user)),
            pluck('challengeStateMap'),
            distinctUntilChanged((_prev, curr) => {
                const isSame = (lastChallengeState == JSON.stringify(curr));
                // Manually save the last challenge state as the ChallengeStateMap is a reference (and thous _prev will not work as expected)
                lastChallengeState = JSON.stringify(curr);
                return isSame;
            }),
            // Create a new challenge state if it does not exist
            map(challengeState => (challengeState) ? challengeState : new ChallengeStateMap({})),
            // Make sure all subscribers only invokes one pipe
            shareReplay(1)
        );
    }

    private watchPointTriggers() {
        // Get challenges
        return this.moteEventService.watchMoteEvents().subscribe(ev => {
            // Find challenge listening for this type
            let challenges = this.challengeMap[ev.type];
            // If no challenges registered for this event, nothing to do
            if(!challenges) return;
            // Check if user is registered
            if(!this.user) return console.warn("Unable to save challenge points: No user signed in");

            if(ev.type == MoteEventType.QUIZ_POINT) {
                this.updateQuizChallenge(<QuizPointMoteEvent>ev, challenges);
            }
            else {
                this.updateMoteChallenge(ev, challenges);
            }
        });
    }

    /**
     * Handle incoming events
     */
    private handleMoteEvents(): Subscription {
        return this.moteEventService.watchMoteEvents().subscribe((ev) => {
            switch(ev.type) {
                case MoteEventType.QUIZ_REQUESTED:
                    // Emits `QUIZ_STARTED` event, unless the user is engaged in group play.
                    if(this.user?.groupKey) break;

                    const event = { type: MoteEventType.QUIZ_STARTED, mote: ev.mote };
                    this.moteEventService.emitMoteEvent(event);
                    break;
                case MoteEventType.QUIZ_SKIPPED:
                    // Award 0 points if a user skips a quiz
                    this.giveZeroQuizPoints(ev.mote);
                    break;
            }
        });
    }

    /**
     * Update a challenge state with the goal of QUIZ_POINT's
     */
    private updateQuizChallenge(ev: QuizPointMoteEvent, challenges: Challenge[]) {
        // Find the challenge that got new points
        let challenge = challenges.find(challenge => challenge.key == ev.options.challengeKey);
        if(!challenge) return console.warn("No challenge with key from mote event exists??", ev, challenges);
        this.addUserChallengePoints(challenge, ev);
        if(this.user?.groupKey) this.addGroupMemberChallengePoints(challenge, ev);
    }

    /**
     * Update a challenge state of all challenges with a goal of unlocking motes
     */
    private updateMoteChallenge(ev: MoteEvent, challenges: Challenge[]) {
        // Add points to all challenges that count this kind of mote event as a point
        for(let challenge of challenges) {
            // If challenge is not configured to handle this kind of point / event type - do nothing
            if(challenge.pointType != ev.type) continue;
            // Count up challenge point by 1
            this.addUserChallengePoints(challenge, ev);
            if(this.user?.groupKey) this.addGroupMemberChallengePoints(challenge, ev);
        }
    }

    /**
     * Determine how many challenge points to add to user challenge state
     * and save the state in user
     */
    private addUserChallengePoints(challenge: Challenge, ev: MoteEvent | QuizPointMoteEvent) {
        let challengeState = this.user.challengeStateMap.states[challenge.key];
        // Init challenge state if it does not already exist
        if(!challengeState) challengeState = this.user.challengeStateMap.states[challenge.key] = new ChallengeState({ challenge: challenge.key });

        const moteKey = this.getMoteKeyFromEvent(ev);

        // Allow multiple points in QUIZ_POINT challenges (but not in other types of challenges like MOTE_UNLOCK)
        const allowMultiplePoints = (challenge.pointType == MoteEventType.QUIZ_POINT);
        const questionAnswered = (challengeState.correctAnswersMap[moteKey] != null);

        // Check if points already given for this quiz question
        if(!allowMultiplePoints && questionAnswered) return console.warn("Point already given for this challenge entry", moteKey, challengeState.pointMap[moteKey]);

        // Calculate points
        if(ev.type == MoteEventType.QUIZ_POINT) {
            challengeState = this.calculatePoints(challengeState, ev.mote, ev as QuizPointMoteEvent);
        } else {
            challengeState = this.calculatePoints(challengeState, ev.mote);
        }

        // Update user points in challenge for easy access in view
        challenge.userPoints = challengeState.points;

        // Save user challenge state in database
        this.userService.updateChallengePoints(this.user.key, challengeState.challenge, challengeState);
    }

    /**
     * Determine how many challenge points to add to group member challenge state and save in database
     */
    private addGroupMemberChallengePoints(challenge: Challenge, ev: MoteEvent | QuizPointMoteEvent) {
        const groupMember = this.groupService.getCurrentMember();
        let challengeState = groupMember.challengeStateMap.states[challenge.key];
        // Init challenge state if it does not already exist
        if(!challengeState) challengeState = groupMember.challengeStateMap.states[challenge.key] = new ChallengeState({ challenge: challenge.key });

        const moteKey = this.getMoteKeyFromEvent(ev);

        // Allow multiple points in QUIZ_POINT challenges (but not in other types of challenges like MOTE_UNLOCK)
        const allowMultiplePoints = (challenge.pointType == MoteEventType.QUIZ_POINT);
        const isQuestionAnswered = (challengeState.correctAnswersMap[moteKey] != null);

        // Check if points already given for this quiz question
        if(!allowMultiplePoints && isQuestionAnswered) return console.warn("Point already given for question in the group quiz", moteKey, challengeState.pointMap[moteKey]);

        // Calculate points
        if(ev.type == MoteEventType.QUIZ_POINT) {
            challengeState = this.calculatePoints(challengeState, ev.mote, ev as QuizPointMoteEvent);
        } else {
            challengeState = this.calculatePoints(challengeState, ev.mote);
        }
        // Save updated challengeState to database
        this.groupService.setCurrentMemberChallengeState(this.user.groupKey, challengeState.challenge, challengeState);
    }

    /**
     * Get a `Mote` key from a `MoteEvent` or `QuizPointMoteEvent`
     * @param ev The event
     * @returns The key for the mote included in the event
     */
    private getMoteKeyFromEvent(ev: MoteEvent | QuizPointMoteEvent): string {
        if(!ev.mote) console.warn("No mote given in challenge mote event");
        return ev.mote ? ev.mote.key : 'undefined-quiz-key';
    }

    /**
     * Calculate points for a challenge based on a `Mote` or `QuizPointMoteEvent`
     * @param challengeState The current `ChallengeState`
     * @param mote The related `Mote`
     * @param ev The event
     * @returns A updated `ChallengeState` object with points added
     */
    private calculatePoints(challengeState: ChallengeState, mote: Mote, ev?: QuizPointMoteEvent): ChallengeState {
        // Add bonus points if answer was correct
        const bonusPoints = (ev && ev.options.isCorrectAnswer) ? ev.options.bonusPoints : 0;
        // Add points from event + bonus (or default to 10 in non quiz challenges)
        const points = ev ? (ev.options.points + bonusPoints) : 10;
        const existingPoints = challengeState.pointMap[mote.key] ? challengeState.pointMap[mote.key] : 0;

        // Register points of answered question (including bonus points)
        challengeState.pointMap[mote.key] = points + existingPoints;
        challengeState.correctAnswersMap[mote.key] = (!ev || ev.options.isCorrectAnswer) ? true : false;

        // Add points to total
        challengeState.points += points;
        challengeState.bonusPoints += bonusPoints;

        // Increase the challenge level reached by user to highest mote order (set mote order/stage higher than the current level)
        if(ev && ev.options.isCorrectAnswer) challengeState.level = Math.max(mote.order, challengeState.level);

        return challengeState;
    }

    /**
     * Get the challenge state of the current user if any
     */
    public getChallengeState(challengeKey: string): ChallengeState {
        if(!this.user) return null;
        return this.user.challengeStateMap.getChallengeState(challengeKey);
    }

    /**
     * Reset the challenge state of the given user and notify listeners
     * OBS: Only for debug purposes
     */
    public resetChallengeState(user: User) {
        // Apply change immediately
        user.challengeStateMap = new ChallengeStateMap({});
        this.userService.resetChallengeStateMap(user.key);
    }

    /**
     * Save challenge to the database
     */
    public saveChallenge(challenge: Challenge): Promise<any> {
        if(!challenge.project) {
            console.error("Won't save challenge without a project", challenge);
            return Promise.reject("Could not save challenge - no project key");
        }

        // Save changes in media database
        return this.db.database.object(MotesPaths.CHALLENGE_DB_PATH(challenge.key)).set(challenge.toJson());
    }

    /**
     * Delete challenge from the database
     */
    public deleteChallenge(challenge: Challenge): Promise<any> {
        if(!challenge || !challenge.key) return Promise.reject("Can not delete non existing (null) challenge");
        // Save changes in media database
        return this.db.database.object(MotesPaths.CHALLENGE_DB_PATH(challenge.key)).remove();
    }

    /**
     * Watch updates of a specific challenge from the database
     */
    public watchChallenge(challengeKey: string): Observable<Challenge> {
        return this.db.database.object(MotesPaths.CHALLENGE_DB_PATH(challengeKey)).snapshotChanges().pipe(map(snap => {
            return new Challenge(snap.payload.val(), snap.key);
        }));
    }

    /**
     * Get a specific challenge from the database one time
     */
    public getChallenge(challengeKey: string): Promise<Challenge> {
        return this.watchChallenge(challengeKey).pipe(first()).toPromise().catch(() => null);
    }

    /**
    * Watch all challenges from the challenge key map or all challenges without filtering
    */
    public watchAllChallenges(onlyPublished = true): Observable<Challenge[]> {
        let query = (onlyPublished) ? (ref) => ref.orderByChild('published').equalTo(true) : undefined;

        return this.db.database.list(MotesPaths.CHALLENGE_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            // Get payload json from firebase and add key in data (to be compatible with current impl)
            let challenges = snapList.map((snap) => new Challenge(snap.payload.val(), snap.key));
            return challenges;
        }));
    }

    /**
     * Watch updates of a specific group of challenge from the database
     */
    public watchChallenges(challengeKeys: string[]): Observable<Challenge[]> {
        if(!challengeKeys || challengeKeys.length <= 0) return of([]);

        let observables = challengeKeys.map(key => this.watchChallenge(key));
        // Merge all query observable outputs
        return combineLatest(...observables);
    }

    /**
     * Watch all challenges from the specific project project or watch all challenges without filtering
     */
    public watchProjectChallenges(projectKey: string = '', onlyPublished = true): Observable<Challenge[]> {
        // console.log("Watch challenges: ", projectKey);
        let query = (onlyPublished) ? (ref) => ref.orderByChild('project').equalTo(projectKey) : null;

        return this.db.database.list(MotesPaths.CHALLENGE_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            // Get payload json from firebase and add key in data (to be compatible with current impl)
            let challenges = snapList.map((snap) => new Challenge(snap.payload.val(), snap.key));
            // Filter out published
            return challenges.filter(challenge => (challenge.published));
        }));
    }

    /**
     * Watch tour challenge if there is one. Otherwise return Observable<undefined>
     */
    public watchTourChallenge(tourKey: string, onlyPublished: boolean = true): Observable<Challenge> {

        let query = (ref) => ref.orderByChild('tourKey').equalTo(tourKey);

        // Fetch list of tour challenges
        return this.db.database.list(MotesPaths.CHALLENGE_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            // Get payload json from firebase and add key in data (to be compatible with current impl)
            let challenges = snapList.map((snap) => new Challenge(snap.payload.val(), snap.key));
            // Return first published challenge
            return onlyPublished ? challenges.find(challenge => challenge.published) : (challenges.length > 0 ? challenges[0] : undefined);
        }));
    }

    /**
     * Get tour challenge if there is one. Otherwise return Promise<undefined>
     */
    public async getTourChallenge(tourKey: string): Promise<Challenge> {
        return this.watchTourChallenge(tourKey).pipe(first()).toPromise();
    }

    /**
     * Watch all challenges from the specific project project or watch all challenges without filtering
     */
    public watchActiveProjectChallenges(projectObs: Observable<ProjectChangeEv>, onlyPublished: boolean = true): Observable<Challenge[]> {
        return projectObs.pipe(switchMap(ev => {
            // console.log("Got project ", ev);
            if(!ev.project) return [];
            return this.watchProjectChallenges(ev.project.key, onlyPublished);
        }));
    }

    public requestQuiz(mote: Mote): void {
        const event = {
            type: MoteEventType.QUIZ_REQUESTED,
            mote: mote
        };

        this.moteEventService.emitMoteEvent(event);
    }

    /**
     * Send completed (and available) status of mote quiz question to listeners (typically detail-mote with quiz buttons)
     */
    public emitQuizQuestionComplete(mote: Mote, completed: boolean): void {
        // Use last emitted value of available
        let available = this.quizQuestionState.value?.available || false;
        this.quizQuestionState.next({ mote, available, completed });
    }

    /**
     * Send available (and completed) status of mote quiz question to listeners (typically detail-mote with quiz buttons)
     */
    public emitQuizQuestionAvailable(mote: Mote, available: boolean): void {
        // Use last emitted value of available
        let completed = this.quizQuestionState.value?.completed || false;
        this.quizQuestionState.next({ mote, available, completed });
    }

    /**
     * Watch the status (available and completed state) of a quiz question (of a specific mote)
     * @param moteKey Mote key to filter quiz question state events of
     * @returns An observable that emits on new quiz question state updates of the given mote key
     */
    public watchQuizQuestionState(moteKey: string): Observable<QuizQuestionState> {
        // Filter out quiz question states only for the given mote
        return this.quizQuestionState.asObservable().pipe(filter(ev => ev?.mote?.key == moteKey));
    }

    /**
     * Give user zero points in a mote quiz question aka mark quiz completed (used for canceling quiz in group mode)
     * @param mote Mote of the quiz question to give user 0 points in
     */
    public giveZeroQuizPoints(mote: Mote) {
        // Create zero point options for event
        let options: PointOptions = {
            points: 0,
            bonusPoints: 0,
            challengeKey: mote?.quiz?.challenge,
            isCorrectAnswer: true  // OBS: We have to mark this as true in order to register quiz done
        }
        // Give user 0 points
        this.moteEventService.emitMoteEvent({ type: MoteEventType.QUIZ_POINT, mote: mote, options: options });
    }

    /**
     * Return true if the current quiz / user is part of a group
     */
    public isGroupQuiz(): Boolean {
        return Boolean(this.user?.groupKey);
    }

}
